import React from 'react'
import { router } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import HistoryHeader from '@/components/history/HistoryTabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowUpRight, ArrowDownRight, Minus, ChevronLeft, ChevronRight, Clock, Filter, BedDouble, X, Zap, UserPen, MoonStar, Sunrise, Target } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMoney, formatCount } from '@/lib/format'
import HotelChip from '@/components/groups/HotelChip'
import UnitScopeNote from '@/components/groups/UnitScopeNote'
import { spansHotels } from '@/lib/reporting-scope'
import type { SharedProps } from '@/types/shared'
import type { HistoryTab } from '@/types/history'

/**
 * HISTORY, THE PRICES TAB (docs/plans/2026-10-20-change-safety.md §5.12.2, §5.17.2): every price the
 * engine moved. This is the Change Log, moved here unchanged except for the History heading and
 * tabs and two filters a Changes entry links with: its rooms (`room_type_ids[]`) and its moment
 * (`since`). Both are echoed back by the server in `filters` and shown as chips with a clear link.
 * The link from a change reads "Price changes since then" and never claims the change moved these
 * prices (R2.8): a recompute carries no edit, and one run can apply several.
 */

interface ChangeLogEntry {
  id: number
  date: string
  /**
   * WHICH HOTEL THE CHANGE LANDED ON. Always sent, never conditional on grouping: a change is
   * stamped with the property that owns the room, which is what keeps publishing per listing.
   */
  propertyId?: number | null
  propertyName?: string | null
  roomType: string
  ratePlan: string
  occupancy: number
  oldRate: number
  newRate: number
  source: string
  createdAt: string
  /** When the price moved, server-composed in the hotel's zone: "23:20" today, "20 Oct, 23:20" otherwise. */
  changedAt: string
  changedBy: string | null
  contributors: string[]
}

interface Pagination {
  page: number
  pages: number
  count: number
}

interface RoomTypeOption {
  id: number
  name: string
  propertyId?: number | null
  propertyName?: string | null
}

interface RatePlanOption {
  id: number
  name: string
  propertyId?: number | null
  propertyName?: string | null
}

interface Filters {
  roomTypeId: string | null
  ratePlanId: string | null
  source: string | null
  startDate: string | null
  endDate: string | null
  category?: string | null
  /** From a Changes entry: the rooms it touched. Server-echoed ids. */
  roomTypeIds?: number[]
  /** From a Changes entry: the moment it was saved (ISO 8601), server-echoed. */
  since?: string | null
}

interface TargetPressureEntry {
  id: number
  occurredAt: string
  category: string
  message: string
  severity: string
  eventType: string
  fromState: string | null
  toState: string | null
  flagName: string | null
  flagValue: boolean | null
  reasonCode: string | null
  targetMonth: number
  targetYear: number
  monthName: string
  context: Record<string, unknown>
}

interface ChangeLogProps extends SharedProps {
  tabs: HistoryTab[]
  activeTab: 'prices'
  entries: ChangeLogEntry[]
  pagination: Pagination
  roomTypes: RoomTypeOption[]
  ratePlans: RatePlanOption[]
  filters: Filters
  targetPressureEntries?: TargetPressureEntry[]
  activeCategory?: string
}

// Contributor badge config: displayed alongside the source badge when stages are known.
// Contributors are CATEGORIES, not severities, so we draw from a restrained categorical
// set built only from the sanctioned families (slate + teal + amber + emerald tints).
const contributorConfig: Record<string, { label: string; color: string; bg: string }> = {
  target:      { label: 'Target',   color: 'text-teal-700',    bg: 'bg-teal-100' },
  demand:      { label: 'Demand',   color: 'text-emerald-700', bg: 'bg-emerald-100' },
  seasonal:    { label: 'Seasonal', color: 'text-amber-700',   bg: 'bg-amber-100' },
  day_of_week: { label: 'DOW',      color: 'text-slate-700',   bg: 'bg-slate-100' },
  event:       { label: 'Event',    color: 'text-amber-700',   bg: 'bg-amber-100' },
  gap_filling: { label: 'Gap fill', color: 'text-slate-700',   bg: 'bg-slate-100' },
  override:    { label: 'Override', color: 'text-amber-700',   bg: 'bg-amber-100' },
  // The clearance STAGE ran on this row. Distinct from the `clearance` change SOURCE above: a
  // family sitting on the same rung while another stage moves the price is an ordinary
  // computation that still carries a clearance line in its trace.
  clearance:   { label: 'Clearance', color: 'text-amber-800', bg: 'bg-amber-100' },
}

// Only sources the backend can actually write today: pipeline rows are 'computation', rows
// produced because a manual override applied are 'manual_override', rows written because
// clearance stepped a price DOWN are 'clearance', and rows written because it let the price go
// back to normal are 'clearance_ended'. Clearance keeps the amber it wears on the calendar badge
// and in the day popup; the icon and the word are what separate it from an override at a glance.
//
// THE ENDING IS ITS OWN ROW, IN A NEUTRAL TONE (operator ruling, 2026-09-02). One source for the
// whole transition rendered "£80 → £100 · Clearance": the badge that means "the engine cut this
// price", on the row where it gave one back. It does not wear amber (that is the cut) and it does
// not wear teal (§12 rule 6: a withdrawal is a measurement, not a win); slate is the honest one,
// and the word plus the sunrise are what separate it from a plain recompute at a glance.
const sourceConfig: Record<string, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  computation: { icon: Zap, label: 'Auto', color: 'text-slate-700', bg: 'bg-slate-100' },
  clearance: { icon: MoonStar, label: 'Clearance', color: 'text-amber-800', bg: 'bg-amber-100' },
  clearance_ended: { icon: Sunrise, label: 'Clearance ended', color: 'text-slate-700', bg: 'bg-slate-100' },
  manual_override: { icon: UserPen, label: 'Manual override', color: 'text-amber-700', bg: 'bg-amber-50' },
}

// Every clock on History is the hotel's: the Changes tab's times, the "Since" chip and these. In the
// viewer's own zone a price logged at 20:01 in London read 22:01 beside a "Since 19:30" chip for a
// viewer three hours ahead. Formatting only; an unknown zone falls back to the viewer's.
function inZone(iso: string, options: Intl.DateTimeFormatOptions, timeZone: string | undefined): string {
  const date = new Date(iso)
  try {
    return date.toLocaleString('en-GB', { ...options, timeZone })
  } catch {
    return date.toLocaleString('en-GB', options)
  }
}

function formatDate(iso: string, timeZone: string | undefined): string {
  return inZone(iso, { day: '2-digit', month: 'short', year: 'numeric' }, timeZone)
}

// A stay date is a calendar date ("2027-07-13"), which JavaScript reads as midnight UTC: shown in UTC it
// is that date everywhere, where the viewer's zone moved it to the day before west of London.
function formatStayDate(isoDate: string): string {
  return inZone(isoDate, { day: '2-digit', month: 'short', year: 'numeric' }, 'UTC')
}

function formatTime(iso: string, timeZone: string | undefined): string {
  return inZone(iso, { hour: '2-digit', minute: '2-digit' }, timeZone)
}

// "19 Oct, 18:14" for the `since` chip, in the hotel's own zone (the Changes tab's times are the
// hotel's, so the chip reads the same minute as the entry it came from). Formatting only.
function formatSince(iso: string, timeZone: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
  try {
    return date.toLocaleString('en-GB', { ...options, timeZone })
  } catch {
    return date.toLocaleString('en-GB', options)
  }
}

const STATE_LABELS: Record<string, string> = {
  on_track: 'On track',
  pushing: 'Pushing',
  easing: 'Easing',
  at_floor: 'At floor',
  dormant: 'Dormant',
}

const STATE_COLORS: Record<string, string> = {
  on_track: 'text-slate-500',
  pushing: 'text-rose-600',
  easing: 'text-emerald-600',
  at_floor: 'text-amber-600',
  dormant: 'text-slate-600',
}

export default function HistoryPrices({
  currentUser,
  property,
  properties,
  tabs,
  activeTab,
  entries,
  pagination,
  roomTypes,
  ratePlans,
  filters,
  targetPressureEntries = [],
  activeCategory = 'rates',
  reportingScope,
}: ChangeLogProps) {
  const basePath = `${property.basePath}/history/prices`
  // Label rows by hotel only when the feed and its filters actually span hotels, derived from the
  // rows, so an ungrouped property's log is unchanged.
  const showHotels = spansHotels(roomTypes) || spansHotels(entries)
  const leadPropertyId = reportingScope?.leadPropertyId ?? null

  function applyFilters(updates: Partial<Filters> & { page?: number }) {
    const merged = { ...filters, ...updates }
    const params: Record<string, string> = {}
    if (merged.roomTypeId) params.room_type_id = merged.roomTypeId
    if (merged.ratePlanId) params.rate_plan_id = merged.ratePlanId
    if (merged.source) params.source = merged.source
    if (merged.startDate) params.start_date = merged.startDate
    if (merged.endDate) params.end_date = merged.endDate
    if (merged.category) params.category = merged.category
    if (updates.page && updates.page > 1) params.page = String(updates.page)
    // The rooms and the moment a Changes entry linked with stay applied until they are cleared.
    const data: Record<string, string | number[]> = { ...params }
    if (merged.roomTypeIds && merged.roomTypeIds.length > 0) data.room_type_ids = merged.roomTypeIds
    if (merged.since) data.since = merged.since
    router.visit(basePath, { data, preserveState: true })
  }

  // Clears only the two filters a Changes entry brought, keeping whatever else was chosen.
  function clearChangeFilters() {
    applyFilters({ roomTypeIds: [], since: null })
  }

  function clearFilters() {
    router.visit(basePath)
  }

  function switchCategory(cat: string) {
    if (cat === 'rates') {
      router.visit(basePath)
    } else {
      router.visit(basePath, { data: { category: cat }, preserveState: false })
    }
  }

  const roomTypeIds = filters.roomTypeIds ?? []
  const since = filters.since ?? null
  const hasChangeFilters = roomTypeIds.length > 0 || !!since
  // The rooms a Changes entry linked with, by the names the room filter already lists.
  const linkedRoomNames = roomTypes.filter((rt) => roomTypeIds.includes(rt.id)).map((rt) => rt.name)

  const hasStandardFilters = !!(filters.roomTypeId || filters.ratePlanId || filters.source || filters.startDate || filters.endDate)
  // The header's Clear is for the bar's own filters. With only a change's rooms and time applied, the
  // chips' own Clear is the one control: two buttons doing the same thing side by side read as two
  // different things. With both kinds applied the header's clears everything, and says so.
  const showHeaderClear = hasStandardFilters
  const headerClearLabel = hasChangeFilters ? 'Clear all' : 'Clear'

  return (
    <OperatorLayout title="History" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <HistoryHeader tabs={tabs} activeTab={activeTab} />

        {/* One pass prices every hotel in the unit, so this one feed is the unit's history. Splitting
            it per listing would put one cycle's work on two pages, neither explaining the other. */}
        <UnitScopeNote
          scope={reportingScope}
          covers="Every change logged here"
          listHotels
          className="animate-slide-in"
        />

        {/* Category chips: Rates / Target Pressure */}
        <div className="animate-slide-in flex items-center gap-2" style={{ animationDelay: '40ms' }}>
          <button
            onClick={() => switchCategory('rates')}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
              activeCategory !== 'target_pressure'
                ? 'bg-primary text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            <Zap className="h-3 w-3" />
            Rates
          </button>
          <button
            onClick={() => switchCategory('target_pressure')}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
              activeCategory === 'target_pressure'
                ? 'bg-primary text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            <Target className="h-3 w-3" />
            Target Pressure
          </button>
        </div>

        {/* Filter Bar: only shown for Rates category */}
        {activeCategory !== 'target_pressure' && <div className="animate-slide-in rounded-xl border border-border/60 bg-white p-4 shadow-sm" style={{ animationDelay: '50ms' }}>
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filters</span>
            {showHeaderClear && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 rounded-lg px-2 text-xs text-muted-foreground"
                onClick={clearFilters}
              >
                <X className="mr-1 h-3 w-3" />
                {headerClearLabel}
              </Button>
            )}
          </div>
          {/* The rooms and the moment a Changes entry linked with ("Price changes since then"). */}
          {hasChangeFilters && (
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="change-filter-chips">
              {roomTypeIds.length > 0 && (
                <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                  <BedDouble className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">
                    {linkedRoomNames.length > 0 ? `Rooms: ${linkedRoomNames.join(', ')}` : 'Rooms from that change'}
                  </span>
                </span>
              )}
              {since && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                  <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
                  Since {formatSince(since, property.timezone)}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 rounded-lg px-2 text-xs text-muted-foreground"
                onClick={clearChangeFilters}
                aria-label="Clear the rooms and time from that change"
              >
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[150px]">
              <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Room Type
              </Label>
              <Select
                value={filters.roomTypeId || ''}
                onValueChange={(v) => applyFilters({ roomTypeId: v === 'all' ? null : v })}
              >
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue placeholder="All Room Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Room Types</SelectItem>
                  {roomTypes.map((rt) => (
                    <SelectItem key={rt.id} value={String(rt.id)}>
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{rt.name}</span>
                        {showHotels && (
                          <HotelChip
                            name={rt.propertyName}
                            propertyId={rt.propertyId}
                            isLead={leadPropertyId != null && rt.propertyId === leadPropertyId}
                          />
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[150px]">
              <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Rate Plan
              </Label>
              <Select
                value={filters.ratePlanId || ''}
                onValueChange={(v) => applyFilters({ ratePlanId: v === 'all' ? null : v })}
              >
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue placeholder="All Rate Plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rate Plans</SelectItem>
                  {/* Two hotels legitimately hold a plan of the same NAME, so a unit-wide plan
                      filter is ambiguous until it says whose plan it is. */}
                  {ratePlans.map((rp) => (
                    <SelectItem key={rp.id} value={String(rp.id)}>
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{rp.name}</span>
                        {showHotels && (
                          <HotelChip
                            name={rp.propertyName}
                            propertyId={rp.propertyId}
                            isLead={leadPropertyId != null && rp.propertyId === leadPropertyId}
                          />
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[140px]">
              <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Source</Label>
              <Select
                value={filters.source || ''}
                onValueChange={(v) => applyFilters({ source: v === 'all' ? null : v })}
              >
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="computation">Computation</SelectItem>
                  <SelectItem value="clearance">Clearance</SelectItem>
                  <SelectItem value="clearance_ended">Clearance ended</SelectItem>
                  <SelectItem value="manual_override">Manual override</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="change-log-start-date" className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Start Date
              </Label>
              <Input
                id="change-log-start-date"
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => applyFilters({ startDate: e.target.value || null })}
              />
            </div>

            <div>
              <Label htmlFor="change-log-end-date" className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                End Date
              </Label>
              <Input
                id="change-log-end-date"
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => applyFilters({ endDate: e.target.value || null })}
              />
            </div>
          </div>
        </div>}

        {/* Target Pressure log */}
        {activeCategory === 'target_pressure' && (
          <div className="animate-slide-in" style={{ animationDelay: '100ms' }}>
            {targetPressureEntries.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
                <Target className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm font-medium text-foreground">No Target Pressure events yet</p>
                <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                  Events appear here when Target Pressure enters or exits a state, or when the demand cap flag changes.
                  State transitions are logged once per change, not on every computation.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {targetPressureEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="whitespace-nowrap">
                          <div className="text-[13px] font-semibold text-foreground">{formatDate(entry.occurredAt, property.timezone)}</div>
                          <div className="text-xs text-muted-foreground">{formatTime(entry.occurredAt, property.timezone)}</div>
                        </TableCell>
                        <TableCell>
                          <span className="text-[13px] font-medium text-foreground">{entry.monthName}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(
                            'text-[10px] border-0 capitalize',
                            entry.eventType === 'state_transition' ? 'bg-slate-100 text-slate-700' : 'bg-teal-100 text-teal-700'
                          )}>
                            {entry.eventType === 'state_transition' ? 'State change' : 'Flag change'}
                          </Badge>
                          {entry.toState && (
                            <span className={cn(
                              'ml-2 text-xs font-semibold',
                              STATE_COLORS[entry.toState] ?? 'text-foreground'
                            )}>
                              → {STATE_LABELS[entry.toState] ?? entry.toState}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-[13px] text-muted-foreground">{entry.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between border-t border-border/40 px-5 py-3">
                    <p className="text-xs text-muted-foreground">
                      Page {pagination.page} of {pagination.pages} ({formatCount(pagination.count)} total)
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="rounded-lg" disabled={pagination.page <= 1}
                        onClick={() => applyFilters({ page: pagination.page - 1, category: 'target_pressure' })}>
                        <ChevronLeft className="mr-1 h-3.5 w-3.5" />Previous
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-lg" disabled={pagination.page >= pagination.pages}
                        onClick={() => applyFilters({ page: pagination.page + 1, category: 'target_pressure' })}>
                        Next<ChevronRight className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Rate change table */}
        {activeCategory !== 'target_pressure' && <div className="animate-slide-in" style={{ animationDelay: '100ms' }}>
          {entries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center shadow-sm">
              <p className="text-sm text-muted-foreground">No rate changes found.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Room Type</TableHead>
                    <TableHead>Rate Plan</TableHead>
                    <TableHead className="text-center">Occ</TableHead>
                    <TableHead className="text-right">Rate Change</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => {
                    const increased = entry.newRate > entry.oldRate
                    const decreased = entry.newRate < entry.oldRate
                    const config = sourceConfig[entry.source] || sourceConfig.computation
                    const SourceIcon = config.icon

                    return (
                      <TableRow key={entry.id}>
                        <TableCell className="text-[13px] font-semibold text-foreground whitespace-nowrap">
                          {formatStayDate(entry.date)}
                        </TableCell>
                        <TableCell className="text-[13px] text-foreground">
                          <span className="flex flex-col gap-0.5">
                            <span>{entry.roomType}</span>
                            {showHotels && (
                              <HotelChip
                                name={entry.propertyName}
                                propertyId={entry.propertyId}
                                isLead={leadPropertyId != null && entry.propertyId === leadPropertyId}
                                className="self-start"
                              />
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-[13px] text-muted-foreground">{entry.ratePlan}</TableCell>
                        <TableCell className="text-center text-[13px] tabular-nums text-muted-foreground">{entry.occupancy}</TableCell>
                        <TableCell className="text-right">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {formatMoney(entry.oldRate, property.currency)}
                            </span>
                            {increased ? (
                              <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />
                            ) : decreased ? (
                              <ArrowDownRight className="h-3.5 w-3.5 text-rose-500" />
                            ) : (
                              <Minus className="h-3.5 w-3.5 text-slate-400" />
                            )}
                            <span className={cn(
                              'text-[13px] font-semibold tabular-nums',
                              increased ? 'text-emerald-600' : decreased ? 'text-rose-600' : 'text-foreground'
                            )}>
                              {formatMoney(entry.newRate, property.currency)}
                            </span>
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            <Badge className={cn('text-[10px] font-semibold gap-1', config.bg, config.color, 'hover:' + config.bg)}>
                              <SourceIcon className="h-3 w-3" />
                              {config.label}
                            </Badge>
                            {entry.source === 'manual_override' && entry.changedBy && (
                              <span className="self-center text-[10px] text-muted-foreground/70">
                                by {entry.changedBy}
                              </span>
                            )}
                            {(entry.contributors ?? [])
                              // A stage chip that merely repeats the source badge beside it is
                              // noise: a clearance-sourced row already says Clearance, and a
                              // Clearance chip beside "Clearance ended" would say the rule both
                              // ran and stopped on the same row.
                              .filter(
                                (c) =>
                                  contributorConfig[c] &&
                                  c !== entry.source &&
                                  !(c === 'clearance' && entry.source === 'clearance_ended'),
                              )
                              .map((c) => {
                                const cc = contributorConfig[c]
                                return (
                                  <Badge
                                    key={c}
                                    className={cn('text-[10px] font-semibold capitalize', cc.bg, cc.color, 'hover:' + cc.bg)}
                                  >
                                    {cc.label}
                                  </Badge>
                                )
                              })}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          <div className="whitespace-nowrap tabular-nums">{entry.changedAt}</div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between border-t border-border/40 px-5 py-3">
                  <p className="text-xs text-muted-foreground">
                    Page {pagination.page} of {pagination.pages} ({formatCount(pagination.count)} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      disabled={pagination.page <= 1}
                      onClick={() => applyFilters({ page: pagination.page - 1 })}
                    >
                      <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => applyFilters({ page: pagination.page + 1 })}
                    >
                      Next
                      <ChevronRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>}
      </div>
    </OperatorLayout>
  )
}
